<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import { StudioTabsInfo } from '@/modules/Studios/types'

const props = defineProps<{
  tabs: StudioTabsInfo[]
  currentTab: number
}>()

const emit = defineEmits<{
  (e: 'update:currentTab', value: number): void
}>()

const route = useRoute()
const router = useRouter()
const isExpansionPanelOpen = ref(false)

const currentTabItem = computed(() => props.tabs[props.currentTab])

const tabModel = computed({
  get: () => props.currentTab,
  set: (value) => {
    emit('update:currentTab', value)
  },
})

const setCurrentTab = (tabItem: StudioTabsInfo) => {
  if (route.query.tab === tabItem.name) {
    isExpansionPanelOpen.value = false
    return
  }
  router.push({
    name: route.name as string,
    params: { id: route.params.id },
    query: { tab: tabItem.name },
    hash: '#studio-tabs',
  })
  tabModel.value = tabItem.id
  isExpansionPanelOpen.value = false
}
</script>

<template>
  <div
    id="studio-tabs"
    class="studio-tabs"
  >
    <v-tabs
      v-if="$vuetify.breakpoint.mdAndUp"
      v-model="tabModel"
      background-color="var(--color-grey-300)"
      color="var(--color-white)"
      grow
      :height="52"
      slider-color="var(--color-grey-900)"
      :slider-size="52"
    >
      <v-tab
        v-for="tabItem in tabs"
        :key="tabItem.id"
        :ripple="false"
        class="tab"
        @click="setCurrentTab(tabItem)"
      >
        {{ tabItem.name }}
      </v-tab>
    </v-tabs>
    <v-expansion-panels
      v-else
      v-model="isExpansionPanelOpen"
      class="expansion-panels"
      flat
    >
      <v-expansion-panel
        active-class="expansion-panel--active"
        class="expansion-panel"
      >
        <v-expansion-panel-header
          class="expansion-panel-header"
          color="var(--color-dark-800)"
        >
          {{ currentTabItem.name }}
        </v-expansion-panel-header>
        <v-expansion-panel-content
          class="expansion-panel-content"
          color="var(--color-grey-300)"
        >
          <v-list class="tabs-list">
            <v-list-item
              v-for="tabItem in tabs"
              :key="tabItem.id"
              :class="{ 'tabs-list-item--active': tabItem.id === currentTabItem.id }"
              class="tabs-list-item"
              @click="setCurrentTab(tabItem)"
            >
              <span class="tabs-list-item--name">{{ tabItem.name }}</span>
            </v-list-item>
          </v-list>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.studio-tabs {
  margin-top: 40px;

  :deep(.v-tabs) {
    overflow: hidden;
    border-radius: 6px;
  }

  .tab {
    position: relative;
    z-index: 2;
    color: var(--color-white) !important;
    font-weight: var(--font-weight-bold);
  }

  @media (max-width: $screen-md) {
    margin-top: 20px;
  }
}

.expansion-panels {
  overflow: hidden;
  border-radius: 6px;
}

.expansion-panel-header {
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: center;
  max-height: 52px;
  padding: 14px;
  color: var(--color-white);
  font-weight: var(--font-weight-bold);
  text-transform: uppercase;

  &::v-deep .v-expansion-panel-header__icon {
    margin-left: 0;

    .v-icon {
      color: var(--color-white) !important;

      &::before {
        @include font-h3;
      }
    }
  }
}

.expansion-panel--active .expansion-panel-header {
  min-height: 52px;
}

.expansion-panel-content::v-deep .v-expansion-panel-content__wrap {
  padding: 0;
}

.tabs-list {
  padding: 0;
  background-color: transparent !important;
}

.tabs-list-item {
  justify-content: center;
  height: 52px;
  padding: 0;

  &--active {
    display: none;
  }

  &--name {
    color: var(--color-white) !important;
    font-weight: var(--font-weight-bold);
    text-transform: uppercase;
  }
}
</style>
